import { useEffect, useState, useMemo } from 'react';
import { Box, Paper, Button, Typography } from '@mui/material';
import { DrawRequest } from '@models/drawRequests/drawRequest';
import { TableRequestModel } from '@models/table/tableRequestModel';
import { TableStateModel } from '@models/table/tableStateModel';
import { useDrawRequestQueryApi } from '@hooks/api/useDrawRequestQueryApi';
import { OrderDirection } from '@enums/orderDirectionEnum';
import DrawRequestCardList from './DrawRequestCardList';
import DrawRequestSortMenu from './DrawRequestSortMenu';
import { getStatusChipColor } from './DrawRequestGridColumns';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useRoutePersistence } from '@hooks/useRoutePersistence';
import { DRAW_REQUEST_PAGINATION_OPTIONS } from '@constants/drawManagerConstants';
import { clearRouteHistory } from '@services/routePersistenceService';
import { PrivateRoutes } from '@enums/privateRouteEnums';

export default function ViewAllDraws() {
  const navigate = useNavigate();
  const location = useLocation();
  const { account, sourceRecId } = useParams<{
    account: string;
    sourceRecId: string;
  }>();
  const { setIntentionalPath } = useRoutePersistence();

  const effectiveLoanRecId = sourceRecId;

  const initialTableState = useMemo(
    () => ({
      ...TableStateModel,
      params: {
        ...TableStateModel.params,
          order: { name: 'drawRequestId', direction: OrderDirection.DESC },
      },
    }),
    []
  );

  const [currentState, setCurrentState] =
    useState<TableRequestModel<string>>(initialTableState);

  const [drawRequests, setDrawRequests] = useState<DrawRequest[]>([]);

  const {
    data: drawRequestsData,
    isLoading,
    refetch,
  } = useDrawRequestQueryApi.useFetchDrawRequests(
    {
      filters: effectiveLoanRecId ? { loanRecId: effectiveLoanRecId } : null,
      params: currentState.params,
    },
    sourceRecId || ''
  );

  useEffect(() => {
    if (drawRequestsData?.data) {
      setDrawRequests(drawRequestsData.data);
    } else if (!isLoading) {
      setDrawRequests([]);
    }
  }, [drawRequestsData, isLoading]);

  const handlePaginationChange = (newState: TableRequestModel<string>) => {
    setCurrentState(newState);
  };

  const handleViewDrawRequest = (drawRequest: DrawRequest) => {
    if (drawRequest.id && account && sourceRecId) {
      navigate(
        PrivateRoutes.REVIEW_DRAW.replace(':account', account)
          .replace(':sourceRecId', sourceRecId)
          .replace(':drawRequestId', drawRequest.id),
        {
          state: {
            from: location.pathname,
            expandedLoan: sourceRecId,
          },
        }
      );
    }
  };

  const totalCount = drawRequestsData?.totalCount || 0;

  const handleBackClick = () => {
    const fromPath = location.state?.from;
    const expandedLoan = location.state?.expandedLoan;

    const isValidFromPath =
      fromPath &&
      fromPath !== PrivateRoutes.ROOT &&
      fromPath !== location.pathname;

    if (isValidFromPath) {
      navigate(fromPath, {
        state: {
          expandedLoan: expandedLoan,
        },
      });
    } else {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        clearRouteHistory();
        setIntentionalPath(PrivateRoutes.ROOT);
        navigate(PrivateRoutes.ROOT, {
          replace: true,
          state: { scrollTo: 'your-loans-tile' },
        });
      }
    }
  };

  const handleSortChange = (name: string, direction: OrderDirection) => {
    setCurrentState(prev => ({
      ...prev,
      params: {
        ...prev.params,
        page: 1,
        order: { name, direction },
      },
    }));
  };

  const currentOrder = currentState.params.order
    ? {
          name: currentState.params.order.name || 'drawRequestId',
        direction: currentState.params.order.direction || OrderDirection.DESC,
      }
    : {
          name: 'drawRequestId',
        direction: OrderDirection.DESC,
      };

  return (
    <Box
      sx={{ maxWidth: '1200px', mx: 'auto', p: 2, backgroundColor: 'teal.200' }}
    >
      <Box
        sx={{
          mt: 8,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Button onClick={handleBackClick} className="back-button">
          {'< Back'}
        </Button>

        <DrawRequestSortMenu
          currentOrder={currentOrder}
          onSortChange={handleSortChange}
        />
      </Box>

      <Typography variant="pageTitle" component="h1" sx={{ mb: 3 }}>
        All Draw Requests
      </Typography>

      <Paper elevation={0} sx={{ p: 0, borderRadius: 2, backgroundColor: 'transparent' }}>
        <DrawRequestCardList
          drawRequests={drawRequests}
          totalCount={totalCount}
          currentPage={currentState.params.page}
          onViewDrawRequest={handleViewDrawRequest}
          onPaginationChange={handlePaginationChange}
          getStatusChipColor={getStatusChipColor}
          rowsPerPage={currentState.params.rowsPerPage || 10}
          rowsPerPageOptions={DRAW_REQUEST_PAGINATION_OPTIONS}
          refetch={refetch}
          isLoading={isLoading}
        />
      </Paper>
    </Box>
  );
}
